import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { MacroToolsStrip } from "@/components/site/MacroToolsStrip";
import { getGoldCorrelation, type CorrelationData } from "@/lib/gold-correlation.functions";
import { cn } from "@/lib/utils";
import { DeskCallout } from "@/components/site/DeskCallout";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-dxy-correlation";
const TITLE = "Gold and DXY Correlation: Real Yields and SMT | XAU Pro Academy";
const DESC =
  "Live gold and US dollar correlation from official Fed data, the real yield driver behind XAUUSD, correlation regimes and the SMT divergence playbook for gold traders.";

const FAQ = [
  { q: "Does gold always fall when the dollar rises?", a: "No. The relationship is usually inverse, but it weakens or turns positive during risk-off shocks and periods of heavy central bank buying. The live table on this page shows the current measured correlation." },
  { q: "What is SMT divergence between gold and DXY?", a: "SMT divergence occurs when two related markets fail to confirm each other at a liquidity level. For example, DXY sweeps a prior high while gold fails to make a matching lower low. It is an early warning of a possible reversal, not an entry on its own." },
  { q: "How do Treasury yields affect the gold price?", a: "Gold pays no yield, so its opportunity cost rises when inflation-adjusted (real) yields rise. Rising 10 year real yields tend to pressure gold; falling real yields tend to support it." },
  { q: "Which leads, gold or the dollar index?", a: "Neither leads consistently. Around US data releases the dollar and yields often reprice first, while gold can lead during geopolitical or central bank driven flows. Treat leadership as conditional." },
  { q: "What is the gold and dollar correlation coefficient?", a: "Over long samples, monthly changes in gold and the broad dollar typically show a negative coefficient. The exact value shifts by regime, which is why we publish rolling 12, 36 and 120 month readings." },
  { q: "Is the Fed Broad Dollar Index the same as DXY?", a: "No. DXY is a six currency index weighted heavily to the euro. The Fed Broad Dollar Index covers a wider trade weighted basket. Both move in the same direction most of the time; we use the Fed series because it is official public data." },
];

export const Route = createFileRoute("/gold-dxy-correlation")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "gold dxy correlation, gold and dollar correlation, gold real yields, xauusd dxy, smt divergence gold, gold vs us dollar" },
      { property: "og:title", content: "Gold and DXY Correlation: Real Yields and SMT Divergence" },
      { property: "og:description", content: "Live gold, dollar and real yield correlations with an institutional SMT divergence playbook." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "Article", headline: "Gold and DXY Correlation: Real Yields, SMT Divergence and the Institutional Playbook", description: DESC, url: `${SITE}${PATH}`, author: { "@type": "Organization", name: "XAU Pro Academy" }, publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE }, datePublished: "2026-10-06", dateModified: "2026-10-06" }) },
      { type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }) },
      { type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: SITE }, { "@type": "ListItem", position: 2, name: "Gold and DXY Correlation", item: `${SITE}${PATH}` }] }) },
    ],
  }),
  loader: () => getGoldCorrelation(),
  staleTime: 60 * 60 * 1000,
  component: Page,
});

const NAV = [
  ["live", "Live readings"],
  ["mechanics", "Mechanics"],
  ["regimes", "Regimes"],
  ["smt", "SMT divergence"],
  ["checklist", "Desk checklist"],
  ["faq", "FAQ"],
] as const;

const fmtC = (v: number | null) => (v == null ? "n/a" : `${v > 0 ? "+" : ""}${v.toFixed(2)}`);
const toneC = (v: number | null) => (v == null ? "var(--muted-foreground)" : v <= -0.3 ? "var(--bias-bear)" : v >= 0.3 ? "var(--bias-bull)" : "var(--muted-foreground)");
const label = (v: number | null) => (v == null ? "Insufficient data" : v <= -0.6 ? "Strong inverse" : v <= -0.3 ? "Moderate inverse" : v < 0.3 ? "Weak or decoupled" : "Positive (abnormal)");

function Page() {
  const data = Route.useLoaderData() as CorrelationData;
  return (
    <Layout>
      <header className="container mx-auto max-w-5xl px-4 pt-14">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Macro correlation engine</div>
        <h1 className="mt-3 font-display text-3xl font-bold leading-tight md:text-5xl">
          Gold and DXY Correlation: Real Yields, SMT Divergence and the Institutional Playbook
        </h1>
        <p className="mt-5 max-w-3xl rounded-xl border border-gold/30 bg-gold-muted/40 p-5 text-base leading-relaxed">
          Gold and the US dollar usually move inversely because gold is priced in dollars. The deeper driver is the
          10 year real yield: when inflation adjusted yields rise, holding gold costs more. The link weakens during
          crises and heavy central bank buying, so correlation must be measured, not assumed.
        </p>
      </header>

      <nav className="sticky top-16 z-30 mt-8 border-y border-border/60 bg-background/80 backdrop-blur">
        <div className="container mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 py-2">
          {NAV.map(([id, t]) => (
            <a key={id} href={`#${id}`} className="whitespace-nowrap rounded-md px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:bg-gold-muted hover:text-gold">{t}</a>
          ))}
        </div>
      </nav>

      <LiveReadings data={data} />
      <Mechanics />
      <Regimes data={data} />
      <Smt />
      <Checklist />

      <div className="container mx-auto max-w-5xl px-4 pt-14">
        <DeskCallout
          eyebrow="Correlation is context. Execution needs structure."
          title="The desk reads the dollar, then waits for gold to confirm."
          body="Macro co-movement never defines an entry on its own. OTAS-01 requires liquidity, displacement and structure on gold itself, and every outcome is logged in the audited archive."
        />
      </div>

      <MacroToolsStrip />

      <section id="faq" className="container mx-auto max-w-5xl scroll-mt-32 px-4 py-12">
        <h2 className="font-display text-2xl font-semibold">Frequently asked questions</h2>
        <div className="mt-4 space-y-3">
          {FAQ.map((f) => (
            <details key={f.q} className="rounded-xl border border-border bg-card p-4">
              <summary className="cursor-pointer font-medium">{f.q}</summary>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          Sources: Federal Reserve Bank of St. Louis FRED series DTWEXBGS, DFII10 and DGS10; XAUUSD monthly spot closes from our market data provider.
          Correlation describes historical co-movement and does not predict future prices. Educational content, not investment advice.
        </p>
      </section>
    </Layout>
  );
}

function LiveReadings({ data }: { data: CorrelationData }) {
  const l = data.latest;
  return (
    <section id="live" className="container mx-auto max-w-5xl scroll-mt-32 px-4 pt-12">
      <h2 className="font-display text-2xl font-semibold">Live correlation readings</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Pearson correlation of monthly changes: gold % versus Fed Broad Dollar Index %, and gold % versus the change in the 10 year real yield.
        {data.stale && " Data feed temporarily unavailable; showing the last good reading."}
      </p>
      {l && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Broad Dollar Index", l.usd.toFixed(2)],
            ["10Y real yield (TIPS)", `${l.real.toFixed(2)}%`],
            ["10Y nominal yield", `${l.nominal.toFixed(2)}%`],
            ["10Y breakeven inflation", `${l.breakeven.toFixed(2)}%`],
          ].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-border bg-card p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</div>
              <div className="mt-1 font-mono text-2xl">{v}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full font-mono text-sm">
          <thead className="border-b border-border text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <tr><th className="p-3 text-left">Window</th><th className="p-3 text-right">Gold vs dollar</th><th className="p-3 text-right">Gold vs real yield</th><th className="p-3 text-left">Reading</th></tr>
          </thead>
          <tbody>
            {data.windows.map((w) => (
              <tr key={w.months} className="border-b border-border/50 last:border-0">
                <td className="p-3">{w.months} months <span className="text-muted-foreground">(n={w.n})</span></td>
                <td className="p-3 text-right" style={{ color: toneC(w.goldUsd) }}>{fmtC(w.goldUsd)}</td>
                <td className="p-3 text-right" style={{ color: toneC(w.goldReal) }}>{fmtC(w.goldReal)}</td>
                <td className="p-3 font-sans text-muted-foreground">{label(w.goldUsd)}</td>
              </tr>
            ))}
            {!data.windows.length && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Awaiting data.</td></tr>}
          </tbody>
        </table>
      </div>
      <RollingChart rows={data.rolling} />
      {l && <p className="mt-2 text-xs text-muted-foreground">Latest official observation: {l.date}. Readings refresh every six hours.</p>}
    </section>
  );
}

function RollingChart({ rows }: { rows: CorrelationData["rolling"] }) {
  if (rows.length < 2) return null;
  const W = 900, H = 220, P = 30;
  const x = (i: number) => P + (i / (rows.length - 1)) * (W - 2 * P);
  const y = (v: number) => H / 2 - v * (H / 2 - 16);
  const path = (k: "goldUsd" | "goldReal") => rows.map((r, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(r[k] ?? 0).toFixed(1)}`).join(" ");
  return (
    <figure className="mt-4 rounded-xl border border-border bg-card p-4">
      <figcaption className="flex flex-wrap gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        <span>Rolling 24 month correlation</span>
        <span className="text-gold">Gold vs dollar</span>
        <span style={{ color: "var(--bias-bull)" }}>Gold vs real yield</span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img" aria-label="Rolling 24 month correlation of gold with the broad dollar and with the 10 year real yield, scaled from minus one to plus one.">
        {[-1, -0.5, 0, 0.5, 1].map((v) => (
          <g key={v}>
            <line x1={P} x2={W - P} y1={y(v)} y2={y(v)} stroke="var(--border)" strokeDasharray={v === 0 ? "0" : "3 4"} />
            <text x={4} y={y(v) + 3} fontSize="10" fill="var(--muted-foreground)" fontFamily="monospace">{v}</text>
          </g>
        ))}
        <path d={path("goldUsd")} fill="none" stroke="var(--gold)" strokeWidth="2" />
        <path d={path("goldReal")} fill="none" stroke="var(--bias-bull)" strokeWidth="2" />
        <text x={P} y={H - 2} fontSize="10" fill="var(--muted-foreground)" fontFamily="monospace">{rows[0].ym}</text>
        <text x={W - P} y={H - 2} fontSize="10" fill="var(--muted-foreground)" fontFamily="monospace" textAnchor="end">{rows.at(-1)!.ym}</text>
      </svg>
    </figure>
  );
}

function Mechanics() {
  return (
    <section id="mechanics" className="container mx-auto max-w-5xl scroll-mt-32 px-4 pt-14">
      <h2 className="font-display text-2xl font-semibold">The mechanical basis</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Denomination effect</div>
          <p className="mt-2 text-sm text-muted-foreground">
            XAUUSD quotes one troy ounce in dollars. When the dollar strengthens, the same ounce costs fewer dollars
            for a buyer holding other currencies, and the quoted price tends to fall. This effect is mechanical but
            usually small relative to the yield channel.
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Opportunity cost</div>
          <p className="mt-2 text-sm text-muted-foreground">
            Gold pays no coupon. A fund comparing gold with Treasuries looks at the yield left after inflation. Higher
            real yields raise the cost of holding gold; lower or negative real yields remove it.
          </p>
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-gold/30 bg-card p-6 text-center font-mono">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Institutional formula</div>
        <div className="mt-3 text-lg md:text-xl">Real Yield = 10Y Treasury Yield - 10Y Breakeven Inflation</div>
        <p className="mx-auto mt-3 max-w-2xl font-sans text-sm text-muted-foreground">
          A nominal yield of 4.5% with 2.5% expected inflation leaves a 2.0% real return. That 2.0% is the hurdle gold must clear.
          The live readings above use the official 10 year TIPS yield (DFII10).
        </p>
      </div>
    </section>
  );
}

const REGIMES = [
  { id: "inverse", name: "Normal inverse", range: "-0.4 to -0.9", body: "Dollar strength and rising real yields pressure gold. US data releases reprice yields first and gold follows. Read DXY liquidity before gold entries.", gold: [0, -1, -0.5, -2, -1.5, -3], usd: [0, 1, 0.5, 2, 1.5, 3] },
  { id: "crisis", name: "Flight to safety", range: "+0.2 to +0.6", body: "During acute risk-off shocks both the dollar and gold attract safe haven demand and rise together. The inverse link temporarily breaks; correlation filters become unreliable.", gold: [0, 1, 0.5, 2, 2.5, 3], usd: [0, 0.8, 1.2, 1.6, 2.4, 2.6] },
  { id: "debasement", name: "Reserve diversification", range: "-0.2 to +0.2", body: "Sustained central bank purchases lift gold even while the dollar is stable or firm. Gold decouples from both the dollar and real yields; structure and positioning matter more than macro correlation.", gold: [0, 1, 1.8, 2.4, 3.2, 3.8], usd: [0, 0.2, -0.2, 0.3, 0, 0.2] },
] as const;

function Regimes({ data }: { data: CorrelationData }) {
  const [sel, setSel] = useState<(typeof REGIMES)[number]["id"]>("inverse");
  const r = REGIMES.find((x) => x.id === sel)!;
  const cur = data.windows.find((w) => w.months === 12)?.goldUsd ?? null;
  const live = cur == null ? null : cur <= -0.4 ? "inverse" : cur >= 0.2 ? "crisis" : "debasement";
  const pts = (arr: readonly number[]) => arr.map((v, i) => `${40 + i * 100},${110 - v * 25}`).join(" ");
  return (
    <section id="regimes" className="container mx-auto max-w-5xl scroll-mt-32 px-4 pt-14">
      <h2 className="font-display text-2xl font-semibold">Correlation regime matrix</h2>
      <p className="mt-2 text-sm text-muted-foreground">Select a regime. The marker shows which regime the latest 12 month reading most resembles. Ranges are schematic guides, not thresholds.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {REGIMES.map((x) => (
          <button key={x.id} type="button" onClick={() => setSel(x.id)} className={cn("rounded-lg border px-4 py-2 text-sm transition", sel === x.id ? "border-gold bg-gold-muted text-gold" : "border-border text-muted-foreground hover:text-foreground")}>
            {x.name}{live === x.id && <span className="ml-2 font-mono text-[10px] text-gold">CURRENT</span>}
          </button>
        ))}
      </div>
      <div className="mt-4 grid gap-4 rounded-xl border border-border bg-card p-5 md:grid-cols-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Typical coefficient {r.range}</div>
          <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>
        </div>
        <svg viewBox="0 0 580 220" className="w-full" role="img" aria-label={`Schematic of gold and dollar paths in the ${r.name} regime.`}>
          <line x1="30" x2="560" y1="110" y2="110" stroke="var(--border)" />
          <polyline points={pts(r.gold)} fill="none" stroke="var(--gold)" strokeWidth="2.5" />
          <polyline points={pts(r.usd)} fill="none" stroke="var(--muted-foreground)" strokeWidth="2" strokeDasharray="6 4" />
          <text x="40" y="210" fontSize="11" fill="var(--gold)" fontFamily="monospace">XAUUSD</text>
          <text x="120" y="210" fontSize="11" fill="var(--muted-foreground)" fontFamily="monospace">USD (dashed) · schematic</text>
        </svg>
      </div>
    </section>
  );
}

function Smt() {
  return (
    <section id="smt" className="container mx-auto max-w-5xl scroll-mt-32 px-4 pt-14">
      <h2 className="font-display text-2xl font-semibold">SMT divergence playbook</h2>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Smart Money Technique (SMT) divergence compares two related markets at the same liquidity level. Because gold
        and the dollar normally move inversely, a DXY high should coincide with a gold low. When DXY sweeps its prior
        high but gold holds above its prior low, one side failed to confirm. That failure often marks liquidity being
        absorbed before a reversal.
      </p>
      <figure className="mt-5 rounded-xl border border-border bg-card p-4">
        <svg viewBox="0 0 900 380" className="w-full" role="img" aria-label="Schematic M15 comparison. DXY makes a higher high that sweeps buy-side liquidity, while gold forms a higher low instead of a lower low. The divergence precedes a gold displacement higher and a break of structure.">
          <text x="20" y="24" fontSize="12" fill="var(--muted-foreground)" fontFamily="monospace">DXY · M15 schematic</text>
          <line x1="300" x2="700" y1="70" y2="70" stroke="var(--muted-foreground)" strokeDasharray="4 4" />
          <text x="705" y="66" fontSize="10" fill="var(--muted-foreground)" fontFamily="monospace">Prior high (BSL)</text>
          <polyline points="40,150 140,110 220,140 320,70 420,130 520,100 600,52 680,120 800,165" fill="none" stroke="var(--muted-foreground)" strokeWidth="2.5" />
          <circle cx="600" cy="52" r="5" fill="var(--bias-bear)" />
          <text x="560" y="40" fontSize="11" fill="var(--bias-bear)" fontFamily="monospace">Higher high: sweep</text>

          <line x1="20" x2="880" y1="190" y2="190" stroke="var(--border)" />
          <text x="20" y="214" fontSize="12" fill="var(--gold)" fontFamily="monospace">XAUUSD · M15 schematic</text>
          <line x1="300" x2="700" y1="330" y2="330" stroke="var(--gold)" strokeDasharray="4 4" opacity="0.6" />
          <text x="705" y="334" fontSize="10" fill="var(--gold)" fontFamily="monospace">Prior low (SSL)</text>
          <polyline points="40,250 140,290 220,262 320,330 420,272 520,300 600,312 680,250 760,230 840,215" fill="none" stroke="var(--gold)" strokeWidth="2.5" />
          <circle cx="600" cy="312" r="5" fill="var(--bias-bull)" />
          <text x="520" y="362" fontSize="11" fill="var(--bias-bull)" fontFamily="monospace">Higher low: no confirmation</text>
          <line x1="700" x2="860" y1="262" y2="262" stroke="var(--bias-bull)" strokeDasharray="3 3" />
          <text x="760" y="256" fontSize="10" fill="var(--bias-bull)" fontFamily="monospace">BOS</text>
          <line x1="600" x2="600" y1="60" y2="304" stroke="var(--gold)" strokeDasharray="2 5" opacity="0.5" />
        </svg>
        <figcaption className="mt-2 text-xs text-muted-foreground">Schematic illustration, not a historical chart. Divergence alone never defines an entry.</figcaption>
      </figure>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {[
          ["1. Divergence", "DXY sweeps a session high; gold fails to make the matching lower low."],
          ["2. Displacement", "Gold prints a strong body close away from the low, leaving an FVG."],
          ["3. Structure", "A body close BOS on M15 confirms intent. Entry from the qualified POI, stop beyond the protected low."],
        ].map(([t, b]) => (
          <div key={t} className="rounded-xl border border-border bg-card p-5">
            <div className="font-display font-semibold">{t}</div>
            <p className="mt-2 text-sm text-muted-foreground">{b}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Checklist() {
  const items = [
    ["Map DXY liquidity", "Mark the Asian and London highs and lows on DXY before the New York session."],
    ["Do not buy gold into untaken DXY lows", "If DXY is still drawing toward an unswept low, gold upside can be a liquidity grab rather than continuation."],
    ["Watch the 13:30 UTC data window", "US data at 08:30 New York reprices the 10 year yield first. Wait for the yield reaction to settle before acting on gold."],
    ["Invalidate without confirmation", "No divergence, no displacement or no BOS means no trade. A missed setup is acceptable."],
  ];
  return (
    <section id="checklist" className="container mx-auto max-w-5xl scroll-mt-32 px-4 pt-14">
      <h2 className="font-display text-2xl font-semibold">Operational desk checklist</h2>
      <ol className="mt-4 space-y-3">
        {items.map(([t, b], i) => (
          <li key={t} className="flex gap-4 rounded-xl border border-border bg-card p-4">
            <span className="font-mono text-gold">0{i + 1}</span>
            <div><div className="font-medium">{t}</div><p className="mt-1 text-sm text-muted-foreground">{b}</p></div>
          </li>
        ))}
      </ol>
      <p className="mt-5 text-sm text-muted-foreground">
        Size every idea with the <Link to="/position-size-calculator" className="text-gold hover:underline">position size calculator</Link>, time it with the{" "}
        <Link to="/gold-trading-sessions" className="text-gold hover:underline">gold trading hours clock</Link>, check positioning in the{" "}
        <Link to="/gold-cot-report" className="text-gold hover:underline">Gold COT Report</Link>, and read the full framework in the{" "}
        <Link to="/gold-trading-strategy" className="text-gold hover:underline">Gold Trading Strategy playbook</Link>.
      </p>
    </section>
  );
}
